import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '../api/client';
import { storage } from '../lib/storage';
import { useTheme } from '../context/ThemeContext';
import fieldiqIcon from '../assets/fieldiq-icon.png';
import fieldiqIconLight from '../assets/fieldiq-icon-light.png';
import fieldiqWordmark from '../assets/fieldiq-wordmark.png';
import fieldiqWordmarkLight from '../assets/fieldiq-wordmark-light.png';

export default function ResetPasswordScreen() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { theme } = useTheme();
  const icon = theme === 'light' ? fieldiqIconLight : fieldiqIcon;
  const wordmark = theme === 'light' ? fieldiqWordmarkLight : fieldiqWordmark;

  const [validating, setValidating] = useState(true);
  const [tokenEmail, setTokenEmail] = useState('');
  const [tokenError, setTokenError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenError('No reset token provided.');
      setValidating(false);
      return;
    }
    api.auth.validateResetToken(token)
      .then((res) => {
        setTokenEmail(res.data.email);
      })
      .catch((err) => {
        setTokenError(err.response?.data?.error || 'This reset link is invalid or has expired.');
      })
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await api.auth.resetPassword({ token, password });
      const newToken = res.data.token;
      if (newToken) {
        // See InviteSetupScreen: `storage.setToken` writes both stores, a raw
        // localStorage write only reaches the one native never reads.
        await storage.setToken(newToken);
      }
      setSuccess(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center p-4">
        <Loader2 className="w-8 h-8 text-fieldiq-accent animate-spin" />
        <p className="text-fieldiq-text mt-3">Validating your reset link...</p>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <img src={icon} alt="FieldIQ" className="w-16 h-16 rounded-2xl mx-auto mb-4" />
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white mb-2">Invalid Reset Link</h2>
          <p className="text-fieldiq-text text-sm mb-6">{tokenError}</p>
          <Link
            to="/forgot-password"
            className="text-fieldiq-accent text-sm hover:underline"
          >
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center p-4">
        <CheckCircle className="w-12 h-12 text-green-400 mb-3" />
        <h2 className="text-lg font-bold text-white mb-1">Password Reset!</h2>
        <p className="text-fieldiq-text text-sm">Redirecting to your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={icon} alt="FieldIQ" className="w-20 h-20 rounded-2xl mx-auto mb-4" />
          <img src={wordmark} alt="FieldIQ" className="h-8 mx-auto mb-2" />
          <p className="text-fieldiq-text">Choose a New Password</p>
        </div>

        {/* Email info */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-lg p-4 mb-6">
          <p className="text-sm text-fieldiq-text">
            Resetting password for <span className="text-white">{tokenEmail}</span>
          </p>
        </div>

        {/* Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* The account's address, for the password manager only. With no
              username field on the form, a saved password is filed under no
              account (or the wrong one) and the login page autofills a stale
              one. Read-only, out of the tab order, and never sent: the save
              posts only the token and the password. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={tokenEmail || ''}
            readOnly
            hidden
            tabIndex={-1}
            aria-hidden="true"
          />
          {error && (
            <div className="bg-fieldiq-danger/20 border border-fieldiq-danger text-fieldiq-danger px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="new-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-4 py-3 text-white focus:border-fieldiq-accent focus:outline-none"
                placeholder="Min. 8 characters"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-text"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-fieldiq-text mb-1">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                name="confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-4 py-3 text-white focus:border-fieldiq-accent focus:outline-none"
                placeholder="Re-enter password"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-text"
              >
                {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-fieldiq-accent text-fieldiq-dark font-bold py-3 rounded-lg hover:bg-fieldiq-accent/90 transition disabled:opacity-50"
          >
            {isSubmitting ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
